/**
 * Tache 1235 (Eric 2026-06-26) — Corbeille des alias, sur la fiche du terme.
 *
 * La garde 1228/1232 retire les alias qui repetent le titre. « Retire » ne
 * doit jamais vouloir dire « perdu » : on les montre ici, et un clic les
 * remet. Sans cette vue, le retrait serait une disparition silencieuse.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowCounterClockwise, Trash } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TermAliasTrash({ termId, onRestored }) {
  const [removed, setRemoved] = useState([]);
  const [appliedAt, setAppliedAt] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!termId) return;
    try {
      const { data } = await axios.get(
        `${API}/api/jargon/${termId}/removed-aliases`,
        { withCredentials: true },
      );
      setRemoved(data?.removed || []);
      setAppliedAt(data?.applied_at || null);
    } catch (e) {
      setRemoved([]);
    }
  }, [termId]);

  useEffect(() => {
    load();
  }, [load]);

  const restore = async (alias) => {
    setBusy(alias);
    try {
      await axios.post(
        `${API}/api/jargon/${termId}/restore-alias`,
        { alias },
        { withCredentials: true },
      );
      toast.success(`Alias « ${alias} » remis dans la fiche`);
      await load();
      onRestored?.();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  if (!removed.length) return null;

  return (
    <div
      className="rounded-lg border border-zinc-200 bg-zinc-50 p-3"
      data-testid="term-alias-trash"
    >
      <div className="flex items-center gap-2 mb-2">
        <Trash size={14} className="text-zinc-500" />
        <span className="text-sm font-semibold text-zinc-800">
          Alias retirés ({removed.length})
        </span>
        {appliedAt && (
          <span className="text-xs text-zinc-500">
            le {String(appliedAt).slice(0, 10)}
          </span>
        )}
      </div>
      <p className="text-xs text-zinc-500 mb-2">
        Retirés parce qu&apos;ils répétaient le titre du terme — ils ne sont pas
        perdus. Un clic les remet.
      </p>
      <ul className="space-y-1.5">
        {removed.map((alias) => (
          <li
            key={alias}
            className="flex items-center justify-between gap-3 text-sm"
            data-testid={`term-alias-trash-row-${alias}`}
          >
            <span className="text-zinc-700 break-words">« {alias} »</span>
            <button
              type="button"
              onClick={() => restore(alias)}
              disabled={busy === alias}
              className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-zinc-300 bg-white text-xs text-zinc-700 hover:bg-zinc-100 disabled:opacity-40"
              data-testid={`term-alias-restore-${alias}`}
            >
              <ArrowCounterClockwise size={12} />
              {busy === alias ? "…" : "Restaurer"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
